// components/VideoPlayer.jsx
// Plyr-based course video player. The parent remounts this component per
// episode (key={episode.id}), so the effect creates one Plyr instance for
// the lifetime of the component and destroys it on unmount.
//
// Controls: play/pause, 5 second rewind and fast-forward, seek bar, time and
// duration, speed menu (0.75 to 2x via the settings gear), captions toggle
// (only when a track exists; Plyr hides it otherwise), PiP, fullscreen.
// No download control anywhere: it is absent from the controls array and
// Plyr's settings menu can only contain captions/quality/speed/loop. The
// media element also carries controlsList="nodownload" and suppresses its
// context menu, which removes the browser's own download affordance and the
// right click save item. All of that is cosmetic, not protection: it only
// covers our own player on our own page and does nothing about a media URL
// opened directly in a tab.
//
// Progress logic stays in the parent: this component forwards Plyr
// 'timeupdate' through onTimeUpdate(player) and handles only the one-time
// resume seek itself. playerRef receives the Plyr instance so the parent
// can read currentTime for Complete and continue.
//
// Media failure recovery: when the media element errors, the player asks the
// parent for a fresh URL through refreshSrc(), swaps it onto the same video
// element and seeks back to where playback died. This exists so a short lived
// signed media URL that expires mid lesson recovers on its own instead of
// leaving a frozen player. Recovery never touches React state and never
// rebuilds Plyr, so the controls, the caption track and playerRef all survive.
//
// refreshSrc: optional () => Promise<string|null>. Resolve with a fresh
// absolute URL, or null when none is available. Throw an Error carrying
// reason === "unauthorized" when the session has expired, so the parent can
// say so rather than have the player retry a request that cannot succeed.
// onUnrecoverableError: optional ({ reason, code }) => void, called once when
// recovery is abandoned. reason is "unauthorized", "exhausted", "refresh" or
// "media"; code is the MediaError code when there was one.

import { useEffect, useRef } from "react";
import Plyr from "plyr";
import "plyr/dist/plyr.css";

// MediaError codes worth a refresh. 2 (network) is what an expired media URL
// looks like once playback has started. 4 (src not supported) is what the
// resource selection algorithm reports when the request is refused before a
// source has been settled on. 3 (decode) and 1 (aborted) are never retried:
// refetching returns the same bytes, and aborted is normally our own teardown.
const RETRYABLE_MEDIA_ERRORS = [2, 4];

// Sized for a long lesson left paused on an expiring URL and then seeked,
// which is a legitimate refresh and can happen several times in one sitting.
const MAX_REFRESH_ATTEMPTS = 4;
const RETRY_BACKOFF_MS = [1000, 3000, 5000, 8000];

// Seconds of real playback past the failure point before the attempt budget is
// handed back. This is the guard that actually stops a retry loop: a URL that
// dies every few seconds never clears its budget, because playback never gets
// this far past the last failure.
const PROGRESS_RESET_SECONDS = 10;

function VideoPlayer({
  src,
  poster,
  captionsUrl,
  episodeId,
  resumeAt = 0,
  autoPlay = false,
  onTimeUpdate,
  playerRef,
  refreshSrc,
  onUnrecoverableError,
}) {
  const videoRef = useRef(null);
  const handlersRef = useRef({ onTimeUpdate, resumeAt, refreshSrc, onUnrecoverableError });
  handlersRef.current = { onTimeUpdate, resumeAt, refreshSrc, onUnrecoverableError };

  useEffect(() => {
    // Deferred init: Plyr construction is pushed one tick out so React
    // StrictMode's dev-only mount, cleanup, mount cycle never races Plyr's
    // asynchronous destroy(). The throwaway first mount clears the timer
    // before it fires, so exactly one instance is ever created.
    let player = null;
    let blobUrl = null;
    let trackEl = null;
    let cancelled = false;

    // Recovery bookkeeping. Plain closure variables rather than refs: they
    // need to live exactly as long as this effect, which is one Plyr instance.
    let attempts = 0;
    let recovering = false;
    let failurePosition = 0;
    let retryTimer = null;
    let pendingLoaded = null;
    let pendingError = null;

    const clearPendingLoaded = () => {
      if (!pendingLoaded) return;
      pendingLoaded.el.removeEventListener("loadedmetadata", pendingLoaded.fn);
      pendingLoaded = null;
    };

    const giveUp = (reason, code) => {
      recovering = false;
      const notify = handlersRef.current.onUnrecoverableError;
      if (typeof notify === "function") notify({ reason, code });
    };

    // Points the same media element at a new URL and puts playback back where
    // it died. Setting src directly takes precedence over the <source> child
    // and leaves the <track> alone, so Plyr, its controls and the captions
    // blob all keep working against the element they were built around.
    const swapSource = (freshUrl, position, wasPlaying) => {
      const video = videoRef.current;
      if (!video) {
        recovering = false;
        return;
      }

      // A fresh src always restarts at zero, so this seek is what keeps the
      // lesson from jumping back to the beginning. The one time resume seek
      // registered below is already spent by now (it has its own guard), so
      // the two never fight over currentTime.
      const onLoaded = () => {
        clearPendingLoaded();
        if (cancelled) return;
        if (position > 0 && (!video.duration || position < video.duration)) {
          video.currentTime = position;
        }
        recovering = false;
        if (wasPlaying) {
          const started = video.play();
          // Autoplay policy can reject this; the viewer can still press play.
          if (started && typeof started.catch === "function") started.catch(() => {});
        }
      };

      clearPendingLoaded();
      video.addEventListener("loadedmetadata", onLoaded);
      pendingLoaded = { el: video, fn: onLoaded };

      video.src = freshUrl;
      video.load();
    };

    const recover = (code) => {
      const video = videoRef.current;
      if (cancelled || recovering || !video) return;

      if (!RETRYABLE_MEDIA_ERRORS.includes(code)) {
        giveUp("media", code);
        return;
      }
      const refresh = handlersRef.current.refreshSrc;
      if (typeof refresh !== "function") {
        giveUp("media", code);
        return;
      }
      if (attempts >= MAX_REFRESH_ATTEMPTS) {
        giveUp("exhausted", code);
        return;
      }

      // Captured now, before anything reloads: this is the only reliable
      // record of where the viewer was. It does not depend on resumeAt.
      const position = video.currentTime;
      const wasPlaying = !video.paused;
      const delay = RETRY_BACKOFF_MS[Math.min(attempts, RETRY_BACKOFF_MS.length - 1)];

      failurePosition = position;
      attempts += 1;
      recovering = true;

      retryTimer = setTimeout(() => {
        retryTimer = null;
        if (cancelled) return;
        Promise.resolve()
          .then(refresh)
          .then((freshUrl) => {
            if (cancelled) return;
            if (!freshUrl) {
              giveUp("refresh", code);
              return;
            }
            swapSource(freshUrl, position, wasPlaying);
          })
          .catch((err) => {
            if (cancelled) return;
            // An expired session is terminal on purpose. Retrying cannot fix
            // it, and the parent needs to be able to say so.
            giveUp(err && err.reason === "unauthorized" ? "unauthorized" : "refresh", code);
          });
      }, delay);
    };

    const timer = setTimeout(async () => {
      const video = videoRef.current;
      if (!video) return;

      // Bound on the media element rather than through Plyr, and before the
      // captions fetch below, so a request refused during init is caught too.
      // Plyr is not constructed until after that fetch resolves, and an error
      // listener registered through it would miss anything that failed first.
      const onMediaError = () => {
        const err = video.error;
        recover(err ? err.code : null);
      };
      video.addEventListener("error", onMediaError);
      pendingError = { el: video, fn: onMediaError };

      // Captions come from the backend proxy (r2.dev cannot serve CORS
      // headers) as an authorized fetch turned into a same-origin blob URL,
      // appended before Plyr constructs so it sees the track at init. Any
      // failure means init without captions; never block playback.
      if (captionsUrl && episodeId != null) {
        try {
          const headers = {};
          const token = localStorage.getItem("sc_token");
          if (token) headers["Authorization"] = `Bearer ${token}`;
          const res = await fetch(
            `${process.env.REACT_APP_API_URL}/api/courses/episodes/${episodeId}/captions.vtt`,
            { headers },
          );
          // Cleanup may have run during the await; bail before touching the
          // DOM so a cancelled run never appends a track.
          if (cancelled) return;
          if (res.ok) {
            blobUrl = URL.createObjectURL(await res.blob());
            const track = document.createElement("track");
            track.kind = "captions";
            track.srclang = "en";
            track.label = "English";
            track.src = blobUrl;
            video.appendChild(track);
            trackEl = track;
          }
        } catch {
          // No captions; the player still initializes below.
        }
      }
      if (cancelled) {
        // Cleanup ran during the fetch; it could not revoke a not yet
        // assigned blob, so revoke here and skip construction.
        if (blobUrl) URL.revokeObjectURL(blobUrl);
        return;
      }

      player = new Plyr(video, {
        controls: [
          "play-large", "play", "rewind", "fast-forward", "progress",
          "current-time", "duration", "settings", "captions", "pip", "fullscreen",
        ],
        settings: ["speed"],
        speed: { selected: 1, options: [0.75, 1, 1.25, 1.5, 1.75, 2] },
        seekTime: 5,
        captions: { active: false, language: "en" },
        autoplay: autoPlay,
      });

      if (playerRef) playerRef.current = player;

      // Seek to the saved position once, as soon as duration is known.
      let resumed = false;
      const resume = () => {
        if (resumed || !player.duration) return;
        resumed = true;
        const at = handlersRef.current.resumeAt;
        if (at > 0 && at < player.duration) player.currentTime = at;
      };
      player.on("loadedmetadata", resume);
      player.on("ready", resume);

      player.on("timeupdate", () => {
        const media = videoRef.current;
        // Hand the attempt budget back only after real forward progress past
        // the last failure. Near the end of a lesson this may never happen,
        // which errs toward not retrying and is the safe direction to err in.
        if (
          attempts > 0 &&
          !recovering &&
          media &&
          media.currentTime >= failurePosition + PROGRESS_RESET_SECONDS
        ) {
          attempts = 0;
          failurePosition = 0;
        }
        handlersRef.current.onTimeUpdate?.(player);
      });
    }, 0);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (retryTimer) clearTimeout(retryTimer);
      clearPendingLoaded();
      if (pendingError) {
        pendingError.el.removeEventListener("error", pendingError.fn);
        pendingError = null;
      }
      if (playerRef) playerRef.current = null;
      if (player) player.destroy();
      if (trackEl) trackEl.remove();
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
    // Mounted once per episode via the parent's key; see header comment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="scad-player">
      {/* No crossOrigin: the video stays an opaque request with no CORS
          requirement. The caption track is appended imperatively in the
          effect as a same-origin blob URL. */}
      <video
        ref={videoRef}
        playsInline
        poster={poster || undefined}
        controlsList="nodownload"
        onContextMenu={(e) => e.preventDefault()}
      >
        <source src={src} type="video/mp4" />
      </video>
      <style>{`
        .scad-player .plyr {
          --plyr-color-main: #2563EB;
          --plyr-video-background: #000;
          --plyr-video-control-color: #fff;
          --plyr-video-control-color-hover: #fff;
          --plyr-menu-background: #fff;
          --plyr-menu-color: #1a1a2e;
          --plyr-menu-radius: 8px;
          --plyr-tooltip-background: #1a1a2e;
          --plyr-tooltip-color: #F5F2EC;
          --plyr-tooltip-radius: 4px;
          --plyr-control-radius: 6px;
          --plyr-font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
          --plyr-font-weight-regular: 500;
          --plyr-font-weight-bold: 700;
          aspect-ratio: 16 / 9;
          width: 100%;
        }
        .scad-player .plyr video {
          object-fit: contain;
        }
        .scad-player .plyr__menu__container {
          box-shadow: 0 4px 16px rgba(26, 26, 46, 0.12);
        }
        .scad-player .plyr__control--overlaid {
          background: #2563EB;
        }
      `}</style>
    </div>
  );
}

export default VideoPlayer;
